import React, { useEffect, useState } from 'react';
import { objectionsAPI } from '../../services/api';
import { Button, Card, Modal } from '../../components/ui';
import toast from 'react-hot-toast';

const inp = { width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', outline: 'none', background: '#fff', boxSizing: 'border-box', transition: 'border-color 0.15s' };
const sel = { ...inp, cursor: 'pointer' };

const CATEGORIES = ['Fee', 'Timing', 'Authority', 'Competition', 'Relevance', 'Trust', 'Logistics', 'Other'];
const CATEGORY_ICONS = {
  Fee: '💰', Timing: '⏰', Authority: '👔', Competition: '🏆',
  Relevance: '🎯', Trust: '🤝', Logistics: '🏗️', Other: '💬'
};
const CATEGORY_COLORS = {
  Fee: '#fef3c7', Timing: '#ffedd5', Authority: '#ede9fe', Competition: '#fce7f3',
  Relevance: '#dcfce7', Trust: '#dbeafe', Logistics: '#f1f5f9', Other: '#f0fdf4'
};
const CATEGORY_TEXT = {
  Fee: '#92400e', Timing: '#9a3412', Authority: '#5b21b6', Competition: '#9d174d',
  Relevance: '#166534', Trust: '#1e40af', Logistics: '#475569', Other: '#166534'
};

const EMPTY_FORM = {
  title: '', category: 'Other', objection_text: '', default_response: '', is_active: true, order: 0
};

export default function ObjectionsPage() {
  const [objections, setObjections] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [modal, setModal]           = useState(false);
  const [editing, setEditing]       = useState(null);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [saving, setSaving]         = useState(false);
  const [filterCat, setFilterCat]   = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await objectionsAPI.getAllAdmin();
      setObjections(res.data?.data || []);
    } catch {
      toast.error('Failed to load objections');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setModal(true);
  };

  const openEdit = (obj) => {
    setEditing(obj._id);
    setForm({
      title:            obj.title,
      category:         obj.category || 'Other',
      objection_text:   obj.objection_text,
      default_response: obj.default_response || '',
      is_active:        obj.is_active,
      order:            obj.order || 0,
    });
    setModal(true);
  };

  const save = async () => {
    if (!form.title.trim() || !form.objection_text.trim()) {
      toast.error('Title and objection text are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title:            form.title.trim(),
        category:         form.category,
        objection_text:   form.objection_text.trim(),
        default_response: form.default_response.trim() || null,
        is_active:        form.is_active,
        order:            parseInt(form.order) || 0,
      };
      if (editing) {
        const res = await objectionsAPI.update(editing, payload);
        setObjections(prev => prev.map(o => o._id === editing ? res.data?.data : o));
        toast.success('Objection updated');
      } else {
        const res = await objectionsAPI.create(payload);
        setObjections(prev => [...prev, res.data?.data]);
        toast.success('Objection added to playbook');
      }
      setModal(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  const toggleActive = async (obj) => {
    try {
      const res = await objectionsAPI.update(obj._id, { is_active: !obj.is_active });
      setObjections(prev => prev.map(o => o._id === obj._id ? res.data?.data : o));
      toast.success(obj.is_active ? 'Deactivated' : 'Activated');
    } catch {
      toast.error('Failed to update');
    }
  };

  const filtered = filterCat ? objections.filter(o => o.category === filterCat) : objections;

  const grouped = CATEGORIES.reduce((acc, cat) => {
    const items = filtered.filter(o => o.category === cat);
    if (items.length) acc[cat] = items;
    return acc;
  }, {});

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 0' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #1B2A6B 0%, #2d4299 100%)', borderRadius: 16, padding: '24px 28px', marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#fff', margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            ⚔️ Objection Playbook
          </h1>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', marginTop: 6, margin: '6px 0 0', lineHeight: 1.5, maxWidth: 500 }}>
            Manage common sales objections and default responses. Team members can generate AI-powered replies during lead interactions.
          </p>
          <div style={{ display: 'flex', gap: 16, marginTop: 12 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#fff' }}>{objections.length}</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>Total</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#4ade80' }}>{objections.filter(o => o.is_active).length}</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>Active</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#fbbf24' }}>{CATEGORIES.filter(c => objections.some(o => o.category === c)).length}</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>Categories</div>
            </div>
          </div>
        </div>
        <Button variant="primary" onClick={openCreate}
          style={{ background: '#fff', color: '#1B2A6B', fontWeight: 700, border: 'none', padding: '10px 20px', whiteSpace: 'nowrap', flexShrink: 0 }}>
          + Add Objection
        </Button>
      </div>

      {/* Filter chips */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
        <button onClick={() => setFilterCat('')}
          style={{ fontSize: 12, fontWeight: 600, padding: '5px 14px', borderRadius: 20, border: '1.5px solid', borderColor: !filterCat ? '#1B2A6B' : '#e2e8f0', background: !filterCat ? '#1B2A6B' : '#fff', color: !filterCat ? '#fff' : '#64748b', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}>
          All ({objections.length})
        </button>
        {CATEGORIES.map(cat => {
          const count = objections.filter(o => o.category === cat).length;
          if (!count) return null;
          const active = filterCat === cat;
          return (
            <button key={cat} onClick={() => setFilterCat(active ? '' : cat)}
              style={{ fontSize: 12, fontWeight: 600, padding: '5px 14px', borderRadius: 20, border: '1.5px solid', borderColor: active ? '#1B2A6B' : '#e2e8f0', background: active ? '#1B2A6B' : '#fff', color: active ? '#fff' : '#64748b', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}>
              {CATEGORY_ICONS[cat]} {cat} ({count})
            </button>
          );
        })}
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8', fontSize: 14 }}>Loading…</div>
      ) : Object.keys(grouped).length === 0 ? (
        <div style={{ background: '#fff', borderRadius: 16, border: '1.5px solid #e2e8f0', padding: '60px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>⚔️</div>
          <p style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>No objections yet</p>
          <p style={{ fontSize: 13, color: '#94a3b8' }}>Add your first objection to start building the playbook.</p>
        </div>
      ) : (
        Object.entries(grouped).map(([cat, items]) => (
          <div key={cat} style={{ marginBottom: 28 }}>
            {/* Category header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{ width: 32, height: 32, background: CATEGORY_COLORS[cat] || '#f1f5f9', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>
                {CATEGORY_ICONS[cat]}
              </div>
              <div>
                <span style={{ fontSize: 13, fontWeight: 800, color: CATEGORY_TEXT[cat] || '#475569', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  {cat}
                </span>
                <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 8, fontWeight: 500 }}>
                  {items.length} objection{items.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div style={{ flex: 1, height: 1, background: '#f1f5f9', marginLeft: 4 }} />
            </div>

            <div style={{ display: 'grid', gap: 10 }}>
              {items.sort((a, b) => a.order - b.order).map(obj => (
                <div key={obj._id} style={{
                  background: '#fff',
                  border: `1.5px solid ${obj.is_active ? '#e2e8f0' : '#f1f5f9'}`,
                  borderRadius: 12,
                  padding: '14px 18px',
                  opacity: obj.is_active ? 1 : 0.6,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                  transition: 'box-shadow 0.15s',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* Title row */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>{obj.title}</span>
                        <span style={{ fontSize: 11, color: '#94a3b8', background: '#f8fafc', padding: '1px 7px', borderRadius: 6, border: '1px solid #e2e8f0' }}>#{obj.order}</span>
                        {!obj.is_active && (
                          <span style={{ fontSize: 11, background: '#fef2f2', color: '#ef4444', padding: '1px 8px', borderRadius: 6, fontWeight: 600 }}>Inactive</span>
                        )}
                      </div>
                      {/* Objection text */}
                      <p style={{ fontSize: 12.5, color: '#64748b', fontStyle: 'italic', margin: '0 0 8px', lineHeight: 1.5 }}>
                        "{obj.objection_text}"
                      </p>
                      {/* Default response */}
                      {obj.default_response && (
                        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderLeft: `3px solid ${CATEGORY_COLORS[cat] ? CATEGORY_TEXT[cat] : '#cbd5e1'}`, borderLeftColor: CATEGORY_TEXT[cat] || '#3b82f6', padding: '8px 12px', borderRadius: '0 8px 8px 0' }}>
                          <p style={{ fontSize: 10, fontWeight: 800, color: '#94a3b8', marginBottom: 3, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Default Response</p>
                          <p style={{ fontSize: 12.5, color: '#475569', margin: 0, lineHeight: 1.5 }}>{obj.default_response}</p>
                        </div>
                      )}
                    </div>
                    {/* Actions */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(obj)}
                        style={{ padding: '4px 14px', fontSize: 12, fontWeight: 600 }}>
                        Edit
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => toggleActive(obj)}
                        style={{ padding: '4px 14px', fontSize: 12, fontWeight: 600, color: obj.is_active ? '#ef4444' : '#16a34a', borderColor: obj.is_active ? '#fecaca' : '#bbf7d0', background: obj.is_active ? '#fef2f2' : '#f0fdf4' }}>
                        {obj.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {/* Create / Edit Modal */}
      <Modal open={modal} onClose={() => setModal(false)} title={editing ? '✏️ Edit Objection' : '➕ Add to Playbook'}>
        <div style={{ display: 'grid', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 5 }}>Title <span style={{ color: '#ef4444' }}>*</span></label>
              <input type="text" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                style={inp} placeholder="e.g. Fee is Too High" />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 5 }}>Category</label>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} style={sel}>
                {CATEGORIES.map(c => <option key={c} value={c}>{CATEGORY_ICONS[c]} {c}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 5 }}>
              Objection Text <span style={{ color: '#ef4444' }}>*</span>
              <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 6 }}>— How the contact typically phrases this</span>
            </label>
            <textarea value={form.objection_text} onChange={e => setForm(f => ({ ...f, objection_text: e.target.value }))}
              style={{ ...inp, minHeight: 76, resize: 'vertical' }}
              placeholder="e.g. The fee is too expensive for our students." />
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 5 }}>
              Default Response
              <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 6 }}>— Quick reference (AI generates context-aware ones)</span>
            </label>
            <textarea value={form.default_response} onChange={e => setForm(f => ({ ...f, default_response: e.target.value }))}
              style={{ ...inp, minHeight: 76, resize: 'vertical' }}
              placeholder="e.g. Our fee includes AIAAI certification, 40 GPU hours, guaranteed internship…" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'end' }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 5 }}>Sort Order</label>
              <input type="number" min="0" value={form.order} onChange={e => setForm(f => ({ ...f, order: e.target.value }))} style={inp} />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '9px 12px', background: form.is_active ? '#f0fdf4' : '#f8fafc', border: `1.5px solid ${form.is_active ? '#bbf7d0' : '#e2e8f0'}`, borderRadius: 8, transition: 'all 0.15s' }}>
              <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
                style={{ width: 16, height: 16, accentColor: '#16a34a' }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: form.is_active ? '#16a34a' : '#64748b' }}>
                {form.is_active ? 'Active' : 'Inactive'}
              </span>
              <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>— visible to team</span>
            </label>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 4, borderTop: '1px solid #f1f5f9', marginTop: 4 }}>
            <Button variant="ghost" onClick={() => setModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save Changes' : 'Add to Playbook'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
